/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { isGuestLoggedIn } from 'sarsaparilla/auth';

import { useGetExternalMediaQuery } from 'shared-ui/api/media/media.queries';
import type { ExternalMedia } from 'shared-ui/api/media/media.types';
import { isMediaUrlExpired } from 'shared-ui/api/media/media.utils';
import { useAuthState } from 'shared-ui/auth';

export type AvatarUrls = {
    /** Standard resolution, for small avatars */
    url: string;
    /** High resolution, for large avatars. Falls back to `url` when unavailable. */
    urlX2: string;
};

type CachedAvatar = {
    pictureId: string;
    media: ExternalMedia;
};

/**
 * useGetAvatarUrl returns the avatar URLs for the current user. Fetching is driven by the
 * external media query, so refreshing it (e.g. after the user uploads a new picture)
 * updates the avatar everywhere it is shown.
 *
 * The last result is cached in session storage and shown while the query revalidates on
 * mount, so the avatar renders without a loading flash when crossing into another
 * microfrontend. The URLs are short-lived signed URLs, so an expired cache entry is
 * skipped rather than shown as a broken image.
 */
export function useGetAvatarUrl(): AvatarUrls | undefined {
    const accountPictureId = useAuthState().account?.profile_picture_id;
    const pictureId = isGuestLoggedIn() ? undefined : accountPictureId || undefined;

    const { data } = useGetExternalMediaQuery({
        mediaId: pictureId,
        initialData: () => {
            const cachedAvatar = getCachedAvatar();
            if (!cachedAvatar || cachedAvatar.pictureId !== pictureId) return undefined;
            const { url, url_x2 } = cachedAvatar.media;
            if (isMediaUrlExpired(url) || isMediaUrlExpired(url_x2)) return undefined;
            return cachedAvatar.media;
        },
    });

    // Keep session storage in sync with the query so the next microfrontend can use it.
    // A failed refetch keeps the previous data, so errors don't clear the cache.
    React.useEffect(() => {
        if (!pictureId) {
            removeCachedAvatar();
        } else if (data) {
            setCachedAvatar({ pictureId, media: data });
        }
    }, [pictureId, data]);

    const url = data?.url;
    const urlX2 = data?.url_x2 || url;

    return React.useMemo(
        () => (pictureId && url && urlX2 ? { url, urlX2 } : undefined),
        [pictureId, url, urlX2]
    );
}

const AVATAR_STORAGE_KEY = 'avatar';

function getCachedAvatar(): CachedAvatar | null {
    try {
        const unparsedAvatar = window.sessionStorage.getItem(AVATAR_STORAGE_KEY);
        if (!unparsedAvatar) return null;
        const avatar: Partial<CachedAvatar> = JSON.parse(unparsedAvatar);
        // Ignore entries written in an older shape
        if (
            typeof avatar?.pictureId !== 'string' ||
            typeof avatar.media?.url !== 'string'
        ) {
            return null;
        }
        return avatar as CachedAvatar;
    } catch {
        return null;
    }
}

function setCachedAvatar(avatar: CachedAvatar) {
    try {
        window.sessionStorage.setItem(AVATAR_STORAGE_KEY, JSON.stringify(avatar));
    } catch {
        // Do nothing
    }
}

function removeCachedAvatar() {
    try {
        window.sessionStorage.removeItem(AVATAR_STORAGE_KEY);
    } catch {
        // Do nothing
    }
}
